<script>
  // Svelte
  import { createEventDispatcher } from 'svelte';

  // d3
  import { feature } from 'topojson-client';
  import { geoPath, geoMercator } from 'd3-geo';
  import { scaleLinear } from 'd3-scale';
  import { transition } from 'd3-transition';
  import { easeQuadIn, easeQuadOut } from 'd3-ease';

  // Data
  import cityList from './bigCities.js';
  import countyTopojson from './california-counties-mercator.js';

  // Import
  export let data;

  // Loads the maps
  let countyGeojson = feature(countyTopojson, countyTopojson.objects.counties);

  // Create a lookup for maps
  const mapLookup = countyGeojson.features.reduce((obj, item) => {
    obj[item.properties.COUNTYFP] = item;
    return obj;
  }, {});

  // Create a lookup for the data
  $: dataLookup = data.reduce((obj, item) => {
    obj[item.fips] = item;
    return obj;
  }, {});

  // Configure the mapping tools for cities
  const path = geoPath();
  const mercator = geoMercator()
    .center([-119, 37.4])
    .scale((1 << 18) / (28 * Math.PI))
    .translate([320, 320]);

  // Hover events
  const dispatch = createEventDispatcher();
  let selectedCountyMap = null;

  function selectCounty(e, push = 20) {
    const fips = e.target.getAttribute('data-fips');
    selectedCountyMap = mapLookup[fips];
    transition()
      .selectAll(`.countyarrow:not([data-fips="${fips}"])`)
      .duration(200)
      .ease(easeQuadIn)
      .style('opacity', 0.3);
    dispatch('mapActivateCounty', {
      fips,
    });
  }
  function deselectCounty(e) {
    const fips = e.target.getAttribute('data-fips');
    transition()
      .selectAll(`.countyarrow`)
      .duration(200)
      .ease(easeQuadOut)
      .style('opacity', 1);
    selectedCountyMap = null;
    dispatch('mapDeactivateCounty', {
      fips,
    });
  }

  // manual activation for San Francisco tied to svg rectangle
  function selectCountySf(e, push = 20) {
    const fips = '075';
    selectedCountyMap = mapLookup[fips];
    transition()
      .selectAll(`.countyarrow:not([data-fips="${fips}"])`)
      .duration(200)
      .ease(easeQuadIn)
      .style('opacity', 0.3);
    dispatch('mapActivateCounty', {
      fips,
    });
  }
  function deselectCountySf(e) {
    const fips = '075';
    selectedCountyMap = null;
    transition()
      .selectAll(`.countyarrow`)
      .duration(200)
      .ease(easeQuadOut)
      .style('opacity', 1);
    dispatch('mapDeactivateCounty', {
      fips,
    });
  }

  const changeRange = data.map((d) => Math.abs(d.d_change));
  const minChange = Math.min(...changeRange);
  const maxChange = Math.max(...changeRange);

  const voteRange = data.map((d) => d.votes_2018);
  const minVote = Math.min(...voteRange);
  const maxVote = Math.max(...voteRange);

  const changeScale = scaleLinear()
    .domain([minChange, maxChange])
    .range([1, 70]);

  const voteScale = scaleLinear()
    .domain([minVote, maxVote])
    .range([2, 10]);

  const customCentroids = [
    { Y: 37.6382977158464, X: -121.733472693877, name: 'Alameda' },
    { Y: 38.5972530950612, X: -119.820650965755, name: 'Alpine' },
    { Y: 38.446397625534, X: -120.651100633547, name: 'Amador' },
    { Y: 39.6669315938575, X: -121.600676377719, name: 'Butte' },
    { Y: 38.2046107182891, X: -120.554124304101, name: 'Calaveras' },
    { Y: 39.1775724124524, X: -122.237023080056, name: 'Colusa' },
    { Y: 37.9228745734181, X: -122.017932626587, name: 'Contra Costa' },
    { Y: 41.5431339258473, X: -123.896659684055, name: 'Del Norte' },
    { Y: 38.8702729202962, X: -120.448648394868, name: 'El Dorado' },
    { Y: 36.7581832798608, X: -119.649320273317, name: 'Fresno' },
    { Y: 39.5984051697798, X: -122.392222927151, name: 'Glenn' },
    { Y: 40.6987245444426, X: -123.873278587847, name: 'Humboldt' },
    { Y: 33.0395049925641, X: -115.365315151508, name: 'Imperial' },
    { Y: 36.5111278463946, X: -117.41078480671, name: 'Inyo' },
    { Y: 35.3428505358287, X: -118.729891654563, name: 'Kern' },
    { Y: 36.0753653518943, X: -119.815495702136, name: 'Kings' },
    { Y: 39.0196429385558, X: -122.753186033367, name: 'Lake' },
    { Y: 40.6735994695084, X: -120.594341060635, name: 'Lassen' },
    { Y: 34.3231299003416, X: -118.224529249411, name: 'Los Angeles' },
    { Y: 37.2180359890309, X: -119.762648178484, name: 'Madera' },
    { Y: 38.0726225890745, X: -122.719526904347, name: 'Marin' },
    { Y: 37.5815257635549, X: -119.905510810874, name: 'Mariposa' },
    { Y: 39.44027845768, X: -123.39127054961, name: 'Mendocino' },
    { Y: 37.1918681866723, X: -120.717658649237, name: 'Merced' },
    { Y: 41.5898573898089, X: -120.724966867724, name: 'Modoc' },
    { Y: 37.9389930375571, X: -118.886711781749, name: 'Mono' },
    { Y: 36.2165203557349, X: -121.238453592782, name: 'Monterey' },
    { Y: 38.5064693162253, X: -122.330533495793, name: 'Napa' },
    { Y: 39.3013655569662, X: -120.768780708103, name: 'Nevada' },
    { Y: 33.7031627102212, X: -117.760596443838, name: 'Orange' },
    { Y: 39.097135382634, X: -120.620011026455, name: 'Placer' },
    { Y: 40.0046802276194, X: -120.8385912527, name: 'Plumas' },
    { Y: 33.7436831173155, X: -115.896139244089, name: 'Riverside' },
    { Y: 38.4244778595343, X: -121.33892984632, name: 'Sacramento' },
    { Y: 36.6057119870373, X: -121.074995064028, name: 'San Benito' },
    { Y: 34.8414384844788, X: -116.178461349664, name: 'San Bernardino' },
    { Y: 33.0358463567863, X: -116.733018645411, name: 'San Diego' },
    { Y: 37.7556940623518, X: -122.442984424925, name: 'San Francisco' },
    { Y: 37.9429574706034, X: -121.205016793375, name: 'San Joaquin' },
    { Y: 35.3870961830444, X: -120.403865398168, name: 'San Luis Obispo' },
    { Y: 37.4216815025804, X: -122.329240055604, name: 'San Mateo' },
    { Y: 34.6731793647052, X: -120.016568658389, name: 'Santa Barbara' },
    { Y: 37.2573127064414, X: -121.814006241864, name: 'Santa Clara' },
    { Y: 37.0562824209595, X: -122.001742479141, name: 'Santa Cruz' },
    { Y: 40.7637712166517, X: -122.040525467361, name: 'Shasta' },
    { Y: 39.6138709161754, X: -120.450844626669, name: 'Sierra' },
    { Y: 41.6226518677519, X: -122.240377558717, name: 'Siskiyou' },
    { Y: 38.296246898193, X: -121.841447382662, name: 'Solano' },
    { Y: 38.5869826909938, X: -122.930547401922, name: 'Sonoma' },
    { Y: 37.5591669125753, X: -120.997748653134, name: 'Stanislaus' },
    { Y: 39.0345196128168, X: -121.69483683959, name: 'Sutter' },
    { Y: 40.1257375421346, X: -122.233884757819, name: 'Tehama' },
    { Y: 40.6506935541145, X: -123.112630881151, name: 'Trinity' },
    { Y: 36.2201641655679, X: -118.800471768531, name: 'Tulare' },
    { Y: 38.0275989101621, X: -119.954756719389, name: 'Tuolumne' },
    { Y: 34.5567685039512, X: -119.083529604848, name: 'Ventura' },
    { Y: 38.6866391813931, X: -121.988479377881, name: 'Yolo' },
    { Y: 39.219098098208, X: -121.351242493134, name: 'Yuba' },
  ];
  // Create a lookup for the centroids
  $: centroidLookup = customCentroids.reduce((obj, item) => {
    obj[item.name] = item;
    return obj;
  }, {});

  $: mergeData = countyGeojson.features.map((d) => {
    d.data = dataLookup[d.properties.COUNTYFP];
    d.centroid = centroidLookup[d.properties.NAME];
    return d
  })
</script>

<svg viewBox="0 0 640 640" role="graphics-document">
  <g class="county-layer" role="graphics-object">
    {#each countyGeojson.features as obj}
      <path
        d={path(obj)}
        class="county"
        data-name={obj.properties.NAME}
        data-fips={obj.properties.COUNTYFP}
        fill="#dddddd"
        stroke="white"
        on:mouseover={selectCounty}
        on:mouseout={deselectCounty}
        role="graphics-object"
      />
    {/each}
  </g>
  <g class="hover-layer" role="graphics-object">
    {#if selectedCountyMap}
      <path
        d={path(selectedCountyMap)}
        fill="none"
        stroke-width="1.5"
        stroke="black"
        role="graphics-symbol"
      />
    {/if}
  </g>
  
  <g class="arrows" role="graphics-object">
    {#each mergeData as obj}
      <g
        class="arrow"
        data-fips={obj.properties.COUNTYFP}
        data-county={obj.data.county}
        data-democratic-change={obj.data.d_change}
        data-democratic-votes-2018={obj.data.votes_2018}
        role="graphics-symbol"
      >
        <line
          class="countyarrow"
          x1={mercator([
            obj.centroid.X,
            obj.centroid.Y,
          ])[0]}
          x2={mercator([
            obj.centroid.X,
            obj.centroid.Y,
          ])[0] - changeScale(obj.data.d_change)}
          y1={mercator([
            obj.centroid.X,
            obj.centroid.Y,
          ])[1]}
          y2={mercator([
            obj.centroid.X,
            obj.centroid.Y,
          ])[1]}
          data-fips={obj.properties.COUNTYFP}
          style="stroke-width: {voteScale(
            obj.data.votes_2018
          )}; opacity:1;"
          stroke={obj.data.d_change > 0 ? '#1d81c5' : '#cc3d3d'}
          marker-end="url(#arrow-head-{obj.data.d_change > 0 ? 'blue' : 'red'})"
        />
      </g>
    {/each}
  </g>

  <defs>
    <marker
      id="arrow-head-blue"
      viewBox="0 0 12 12"
      refX="4"
      refY="4"
      markerWidth="9"
      markerHeight="9"
      orient="auto-start-reverse"
      fill='#1d81c5'
    >
      <path d="M 0 0 L 10 4 L 0 8 L 3 4 z" />
    </marker>
    <marker
      id="arrow-head-red"
      viewBox="0 0 12 12"
      refX="4"
      refY="4"
      markerWidth="9"
      markerHeight="9"
      orient="auto-start-reverse"
      fill='#cc3d3d'
    >
      <path d="M 0 0 L 10 4 L 0 8 L 3 4 z" />
    </marker>
  </defs>

  <g class="cities" role="graphics-object">
    {#each cityList as obj}
      <g
        data-name={obj.name}
        transform="translate({mercator([obj.long, obj.lat])})"
        role="graphics-symbol"
      >
        <circle r="4" class="city" />
        <text
          class="text map-label-halo"
          dx={obj.orientation == 'right' ? '8px' : '-8px'}
          dy="6px"
          text-anchor={obj.orientation == 'right' ? 'start' : 'end'}
        >
          {obj.name}
        </text>
        <text
          class="text map-label-text"
          dx={obj.orientation == 'right' ? '8px' : '-8px'}
          dy="6px"
          text-anchor={obj.orientation == 'right' ? 'start' : 'end'}
        >
          {obj.name}
        </text>
        {#if obj.name == 'San Francisco'}
          <rect
            transform="translate(-113,-8)"
            width="113"
            height="16"
            style="fill:rgb(0,0,0,0)"
            on:mouseover={selectCountySf}
            on:mouseout={deselectCountySf}
          />
        {/if}
      </g>
    {/each}
  </g>
  
<g id="manualLegend" transform="translate(330,60)">
    <g id="arrows">
    <line class="st0" x1="73.23" y1="24.26" x2="123.73" y2="24.26"/>
    <polygon class="st1" points="78.73,24.26 91.73,43.75 48.75,24.26 91.73,4.77 	"/>
    <polyline class="st2" points="124.73,46.26 124.73,52.26 49.23,52.26 49.23,46.26 	"/>
    <polyline class="st2" points="133.74,4.77 139.74,4.77 139.74,43.75 133.74,43.75 	"/>
  </g>
  <g id="text">
    <text transform="matrix(1 0 0 1 46.7695 69.2842)"><tspan x="0" y="0" class="st3 st4">Democratic shift</tspan><tspan x="0" y="18" class="st3 st4">in percentage points</tspan></text>
	<g>
		<text transform="matrix(1 0 0 1 146.0099 20.0996)"><tspan x="0" y="0" class="st3 st4">Vote </tspan><tspan x="0" y="18" class="st3 st4">total</tspan></text>
    </g>
  </g>
</svg>